import { useQuery } from '@tanstack/react-query';
import { ArrowLeftRight, BatteryCharging, Boxes, Lock, Pencil } from 'lucide-react';
import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { AssetStatusBadge, DueBadge } from '../components/badges';
import { CheckInDialog, CheckOutDialog, type CheckoutAsset } from '../components/checkout-dialogs';
import { RoomPeopleCard } from '../components/help-desk-cards';
import { AssignedPartsCard } from '../components/part-dialogs';
import { AssetSearch } from '../components/pickers';
import { Badge, Button, Card, CardHeader, EmptyState, ErrorState, Modal, PageHeader, PageLoader, Table, Td, Th } from '../components/ui';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatDate, ROOM_KIND_LABEL } from '../lib/format';
import { isOpenOn } from '../lib/locations';
import type { Checkout, RoomDetail } from '../lib/types';
import { PlaceDates, RoomModal } from './LocationsPage';

export function RoomDetailPage() {
  const { id } = useParams();
  const { can } = useAuth();
  const [editing, setEditing] = useState(false);
  const [picking, setPicking] = useState(false);
  const [checkoutAssets, setCheckoutAssets] = useState<CheckoutAsset[] | null>(null);
  const [checkingIn, setCheckingIn] = useState<Checkout | null>(null);
  const query = useQuery({ queryKey: ['room', id], queryFn: () => api.get<RoomDetail>(`/rooms/${id}`) });

  if (query.isLoading) return <PageLoader />;
  if (query.error || !query.data) return <ErrorState error={query.error} onRetry={() => query.refetch()} />;
  const room = query.data;
  const canWrite = can('TECHNICIAN');
  const open = isOpenOn(room, room.today) && isOpenOn(room.building, room.today);

  return (
    <>
      <PageHeader
        back={{ to: '/locations', label: 'Locations' }}
        title={room.name}
        meta={
          <>
            <Badge>{room.building.name}</Badge>
            {room.building.kind === 'WAREHOUSE' && <Badge tone="violet">Off campus</Badge>}
            <Badge tone="brand">{ROOM_KIND_LABEL[room.kind]}</Badge>
            {room.roomNumber && <Badge>#{room.roomNumber}</Badge>}
            <PlaceDates place={room} />
            {room.closedOn === null && <PlaceDates place={room.building} />}
          </>
        }
        actions={
          canWrite && (
            <>
              <Button icon={<Pencil />} onClick={() => setEditing(true)}>
                Edit
              </Button>
              {open && (
                <Button variant="primary" icon={<ArrowLeftRight />} onClick={() => setPicking(true)}>
                  Check out to this room
                </Button>
              )}
            </>
          )
        }
      />
      {room.notes && <p className="mb-6 text-sm text-slate-600">{room.notes}</p>}

      <div className="space-y-6">
        <RoomPeopleCard roomId={room.id} roomName={room.name} assignments={room.assignments} today={room.today} closed={!open} />

        {room.carts.length > 0 && (
          <Card>
            <CardHeader title="Carts kept here" description="Every device on these carts is kept in this room" />
            <Table>
              <thead>
                <tr>
                  <Th>Cart</Th>
                  <Th>Devices</Th>
                  <Th>Lease</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {room.carts.map((cart) => (
                  <tr key={cart.id}>
                    <Td>
                      <Link to={`/carts/${cart.id}`} className="inline-flex items-center gap-2 font-medium text-slate-900 hover:text-brand-700">
                        <BatteryCharging className="size-4 text-slate-400" />
                        {cart.name}
                      </Link>
                      {cart.status === 'RETIRED' && <Badge className="ml-2">Retired</Badge>}
                    </Td>
                    <Td>
                      {cart.deviceCount}
                      {cart.capacity ? <span className="text-slate-400"> / {cart.capacity}</span> : null}
                    </Td>
                    <Td>
                      {cart.activeLeaseId ? (
                        <Badge tone="amber">
                          <Lock className="size-3" /> On lease
                        </Badge>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card>
        )}

        <Card>
          <CardHeader title="Assigned to this room" description="Checked out to the room itself - displays, shared equipment" />
          {room.activeCheckouts.length === 0 ? (
            <EmptyState title="Nothing assigned to this room" />
          ) : (
            <Table>
              <thead>
                <tr>
                  <Th>Asset</Th>
                  <Th>Since</Th>
                  <Th>Due</Th>
                  {canWrite && <Th className="text-right">Actions</Th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {room.activeCheckouts.map((checkout) => (
                  <tr key={checkout.id}>
                    <Td>
                      <Link to={`/assets/${checkout.asset.id}`} className="font-mono text-xs font-semibold text-brand-800 hover:underline">
                        {checkout.asset.assetTag}
                      </Link>
                      <p className="text-xs text-slate-500">{checkout.asset.name}</p>
                    </Td>
                    <Td className="whitespace-nowrap">{formatDate(checkout.checkedOutAt)}</Td>
                    <Td>
                      <DueBadge dueAt={checkout.dueAt} />
                    </Td>
                    {canWrite && (
                      <Td className="text-right">
                        <Button size="sm" onClick={() => setCheckingIn(checkout)}>
                          Check in
                        </Button>
                      </Td>
                    )}
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card>

        <AssignedPartsCard assignee={{ type: 'ROOM', room }} />

        <Card>
          <CardHeader title="Kept here" description="Devices whose home location is this room, including those on its carts" />
          {room.assets.length === 0 ? (
            <EmptyState icon={<Boxes />} title="No devices kept here" />
          ) : (
            <Table>
              <thead>
                <tr>
                  <Th>Asset tag</Th>
                  <Th>Name</Th>
                  <Th>Status</Th>
                  <Th>Cart</Th>
                  <Th>Assigned to</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {room.assets.map((asset) => (
                  <tr key={asset.id}>
                    <Td>
                      <Link to={`/assets/${asset.id}`} className="font-mono text-xs font-semibold text-brand-800 hover:underline">
                        {asset.assetTag}
                      </Link>
                    </Td>
                    <Td>
                      {asset.name}
                      <p className="text-xs text-slate-500">{asset.serialNumber}</p>
                    </Td>
                    <Td>
                      <AssetStatusBadge status={asset.status} />
                    </Td>
                    <Td className="text-slate-600">
                      {asset.cart ? (
                        <Link to={`/carts/${asset.cart.id}`} className="hover:text-brand-700">
                          {asset.cart.name}
                        </Link>
                      ) : (
                        '—'
                      )}
                    </Td>
                    <Td className="text-slate-600">{asset.activeCheckout?.assigneeLabel ?? '—'}</Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card>
      </div>

      <RoomModal open={editing} onClose={() => setEditing(false)} room={room} />
      <Modal open={picking} onClose={() => setPicking(false)} title={`Check out to ${room.name}`} description="Find an available asset">
        <AssetSearch
          inline
          status={['AVAILABLE']}
          onSelect={(asset) => {
            setPicking(false);
            setCheckoutAssets([asset]);
          }}
        />
      </Modal>
      <CheckOutDialog
        open={!!checkoutAssets}
        onClose={() => setCheckoutAssets(null)}
        assets={checkoutAssets ?? []}
        initialAssignee={{ type: 'ROOM', room }}
      />
      {checkingIn && <CheckInDialog open onClose={() => setCheckingIn(null)} checkout={checkingIn} />}
    </>
  );
}
